import { useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";

import { formatUserNameWithEmailString, getUniqueArray } from "../../utils";
import { BSSelect } from "../reactSelectBSWrapper";
import { FormControlWrapper } from "./formControlWrapper";

const mapDropDownData = ({
	fieldData,
	dropdownData,
	initValuesFn,
	defaultInitValuesFn,
}) => {
	return getUniqueArray(
		fieldData
			.map((fDatum) => {
				const filteredDropdown = dropdownData.filter((dDatum) =>
					initValuesFn(dDatum, fDatum)
				);

				if (filteredDropdown.length) {
					return filteredDropdown;
				}

				return defaultInitValuesFn(fDatum);
			})
			.flat()
			.filter((value) => value !== null)
	);
};
export const emailInitValueFn = ({ id = "", label = "" }, fDatum) => {
	const email = fDatum?.email ? fDatum.email.toLowerCase() : null;
	return id.toLowerCase() === email || label.toLowerCase() === email;
};

export const featureInitValueFn = ({ id = "" }, fDatum) => {
	return id === fDatum.id;
};

export const userInitValueFn = (fDatum) => {
	const formattedString = formatUserNameWithEmailString(fDatum);
	return fDatum
		? {
				value: formattedString,
				label: formattedString,
				id: formattedString,
		  }
		: null;
};

export const emailDefaultInitValueFn = (fDatum) => ({
	value: fDatum?.email,
	label: fDatum?.email,
	id: fDatum?.email,
});

export const siteDefaultInitValueFn = (fDatum) => ({
	value: fDatum?.id,
	label: fDatum?.name,
	id: fDatum?.id,
});

export const featureDefaultInitValueFn = (fDatum) => ({
	value: fDatum?.id,
	label: fDatum?.name,
	id: fDatum?.id,
});

export const tagInitValueFn = ({ id = "", label = "" }, fDatum) =>
	!!fDatum.tags &&
	!!fDatum.tags.find(
		(tag) =>
			id.toLowerCase() === tag.toLowerCase() ||
			label.toLowerCase() === tag.toLowerCase()
	);

export const tagDefaultInitValueFn = (fDatum) =>
	fDatum.tags
		? fDatum.tags.map((tag) => ({
				value: tag,
				label: tag,
				id: tag,
		  }))
		: null;

export const roleInitValueFn = ({ id = "", label = "" }, fDatum) =>
	id.toLowerCase() === fDatum?.toLowerCase() ||
	label.toLowerCase() === fDatum?.toLowerCase();

export const roleDefaultInitValueFn = (fDatum) =>
	fDatum
		? fDatum.map((tag) => ({
				value: tag,
				label: tag,
				id: tag,
		  }))
		: null;

export const tagDropdownDataFn = (selectedRows) =>
	getUniqueArray(
		selectedRows
			.map(({ tags }) => tags || [])
			.flat()
			.sort()
	).map((tag) => ({ id: tag, value: tag, label: tag }));

export const defaultFieldValueFn = ({ selectedValues }) =>
	selectedValues.map(({ value }) => value);

export const tagFieldValueFn = ({ dropdownData, selectedValues }) =>
	dropdownData
		.filter(
			(dData) => !selectedValues.find((sv) => sv.value === dData.value)
		)
		.map(({ value }) => value);

export const tagMessageFn = ({ dropdownData, selectedValues }) => {
	const removedValues = tagFieldValueFn({ dropdownData, selectedValues });
	if (removedValues.length > 0)
		return (
			<>
				The following tags will be removed:{" "}
				<strong>{removedValues.join(", ")}</strong>
			</>
		);
	// TODO there is a good chance this'll confuse people
	else return null;
};

export const formatPillText = (text) =>
	text.toLowerCase().replace(/;/g, "").substring(0, 30).trim();

export const FieldDropdownMulti = ({
	isDisabled,
	isSelectedRowValues = false,
	selectedRows = [],
	error,
	fieldData = [],
	dropdownData = [],
	dropdownDataFn,
	initValuesFn = emailInitValueFn,
	defaultInitValuesFn = emailDefaultInitValueFn,
	fieldValueFn = defaultFieldValueFn,
	isAddValues = false,
	dropdownMessageFn = null,
	// TODO PLACEHOLDER
	placeholder = "",
	autoFocus,
	tooltip,
	title = "",
	setValue = (_value) => {},
}) => {
	const [newPill, setNewPill] = useState("");

	if (dropdownDataFn) {
		dropdownData = dropdownDataFn(selectedRows);
	}

	let initValues = useMemo(
		() =>
			mapDropDownData({
				fieldData: isSelectedRowValues ? selectedRows : fieldData,
				dropdownData,
				initValuesFn,
				defaultInitValuesFn,
			}),
		[selectedRows, dropdownData, fieldData]
	);

	const [dropdownValues, setDropdownValues] = useState(initValues);
	useEffect(() => {
		setValue(
			JSON.parse(JSON.stringify(initValues.map((data) => data.value)))
		);
	}, []);

	// useEffect(() => {
	// 	setDropdownValues(initValues);
	// }, [fieldData]);

	const handleChange = (eValues) => {
		const fieldValues = fieldValueFn({
			dropdownData,
			selectedValues: eValues,
		});
		setValue(fieldValues);
		setDropdownValues(eValues);
	};

	const onKeyDown = (e) => {
		const trimPill = newPill.trim();
		if (
			(e.key === "Enter" || e.key === " ") &&
			trimPill.length > 0 &&
			isAddValues &&
			!dropdownValues.find(({ value }) => value.toLowerCase() == trimPill)
		) {
			let newDropdownData = dropdownValues.concat([
				{ value: trimPill, label: trimPill },
			]);
			setDropdownValues(newDropdownData);
			const fieldValues = fieldValueFn({
				dropdownData,
				selectedValues: newDropdownData,
			});
			setValue(fieldValues);
			setNewPill("");
		}
	};

	return (
		<Form.Group data-testid="test-fieldDropdownMulti">
			<FormControlWrapper title={title} tooltip={tooltip}>
				<BSSelect
					inputValue={newPill}
					value={dropdownValues}
					onChange={handleChange}
					onInputChange={(text) => setNewPill(formatPillText(text))}
					onKeyDown={onKeyDown}
					options={dropdownData}
					placeholder={placeholder}
					inputMode="text"
					isMulti="true"
					isDisabled={isDisabled}
					error={error}
					autoFocus={autoFocus}
				/>
				{error ? (
					<div
						className="invalid-feedback"
						style={{ display: "contents" }}
					>
						Mandatory field
					</div>
				) : null}
				{dropdownMessageFn ? (
					<div className="mt-1">
						{dropdownMessageFn({
							selectedValues: dropdownValues,
							dropdownData,
						})}
					</div>
				) : null}
			</FormControlWrapper>
		</Form.Group>
	);
};
